import { Component, OnInit, signal, inject, WritableSignal, ViewEncapsulation } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { SendUserInvitations } from '../../charging-user-create.model';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Organization } from '../../../../organization/organization-models';
import { Department } from '../../../../department/department.model';
import { ArrayValidators } from 'src/app/shared/validators/array.validator';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { UserInvitationService } from 'src/app/shared/services/user.invitation.service';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { AccessGroup, AccessGroupSearchEvent } from 'src/app/features/access-groups/access-group.model';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatChipGrid, MatChipRow, MatChipInput, MatChipRemove } from '@angular/material/chips';
import { MatInput } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { InputMaskModule } from '@ngneat/input-mask';

interface CreateChargingUserIdTagWithInvitationModalModel {
  organization: FormControl<Organization | null>;
  department: FormControl<Department | null>;
  selectedAccessGroups: FormControl<SelectedAccessGroup[]>;
  emails: FormArray<FormControl<string>>;
  userHasOrganizationCar: FormControl<boolean | null>;
}
interface SelectedAccessGroup {
  identifier: string;
  name: string;
}

@Component({
  selector: 'adm-create-charging-user-id-tag-with-invitation.modal',
  templateUrl: './create-charging-user-id-tag-with-invitation.modal.component.html',
  styleUrl: './create-charging-user-id-tag-with-invitation.modal.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CustomDropDownArrowDirective,
    AdmOrgTypeaheadComponent,
    AdmDepTypeaheadComponent,
    FeedbackInfoComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    MatHint,
    MatError,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    MatInput,
    ReactiveFormsModule,
    AsyncPipe,
    InputMaskModule,
  ],
})
export class CreateChargingUserIdTagWithInvitationModalComponent implements OnInit {
  data = inject<{
    organization;
  }>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<CreateChargingUserIdTagWithInvitationModalComponent>>(MatDialogRef);
  private snackbarService = inject(SnackbarService);
  private loadingService = inject(TopLoaderService);
  private userInvitationService = inject(UserInvitationService);
  private accessGroupService = inject(AccessGroupService);

  public userFormGroup: FormGroup<CreateChargingUserIdTagWithInvitationModalModel> =
    new FormGroup<CreateChargingUserIdTagWithInvitationModalModel>({
      organization: new FormControl<Organization | null>(null, [Validators.required, this.organizationIdValidator]),
      department: new FormControl<Department | null>(null, [Validators.required]),
      selectedAccessGroups: new FormControl<SelectedAccessGroup[]>({ value: [], disabled: true }, { nonNullable: true }),
      emails: new FormArray<FormControl<string>>(new Array<FormControl<string>>(), [ArrayValidators.minLength(1)]),
      userHasOrganizationCar: new FormControl<boolean | null>(false),
    });
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  resetDepartmentTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  departmentsDisabled = signal<boolean>(true);
  public accessGroups = signal<AccessGroup[]>([]);
  public accessGroups$ = toObservable(this.accessGroups);
  // UI state for email input UX
  public emailInputTouched = signal<boolean>(false);
  public invalidEmailInput = signal<boolean>(false);
  // Reactive control for the email input to leverage Angular Material validation state
  public emailsInputControl = new FormControl<string>('', [Validators.email]);
  ngOnInit(): void {
    this.userFormGroup.controls.selectedAccessGroups.setValidators(ArrayValidators.minLength(1));
    this.userFormGroup.controls.userHasOrganizationCar.setValidators(Validators.required);
    this.userFormGroup.controls.userHasOrganizationCar.setValue(false);
    this.userFormGroup.controls.selectedAccessGroups.updateValueAndValidity();
    this.userFormGroup.controls.userHasOrganizationCar.updateValueAndValidity();
    this.userFormGroup.controls.userHasOrganizationCar.valueChanges.subscribe(x => {
      if (this.userFormGroup.controls.organization?.value?.id) {
        this.userFormGroup.controls.selectedAccessGroups.setValue([]);
        this.getAccessGroups(this.userFormGroup.controls.organization.value.id);
      }
    });
  }

  public resetOrganization() {
    this.userFormGroup.patchValue({
      organization: null,
      department: null,
    });
    this.resetDepartmentTrigger.set({ reset: true });
    this.accessGroups.set([]);
    this.departmentsDisabled.set(true);
  }

  remove(email: FormControl<string>) {
    const index = this.userFormGroup.controls.emails.controls.findIndex(x => x.value == email.value);
    this.userFormGroup.controls.emails.removeAt(index);
    this.userFormGroup.controls.emails.updateValueAndValidity();
    // If emails array is now empty and input has been touched, mark as invalid
    if (this.userFormGroup.controls.emails.length === 0 && this.emailInputTouched()) {
      this.emailsInputControl.setErrors({ required: true });
    }
  }
  private addEmail(email: string) {
    const value = (email || '').trim();
    const formControl = new FormControl<string>(value, { validators: [Validators.email], nonNullable: true });
    const emailAlreadyExists = this.userFormGroup.controls.emails.controls.find(x => x.value == value);

    if (formControl.valid && !emailAlreadyExists) {
      this.userFormGroup.controls.emails.push(formControl);
      this.userFormGroup.controls.emails.updateValueAndValidity();
    }
  }
  emailsPaste(event: ClipboardEvent) {
    event.preventDefault();
    if (!event.clipboardData) {
      return;
    }
    event.clipboardData
      .getData('Text')
      .split(/;|,|\n|\s+/)
      .forEach(value => {
        this.addEmail(value);
      });
  }
  add(ev: MatChipInputEvent | Event): void {
    const event = ev as never as MatChipInputEvent;
    this.addEmail(event.value);
    event.chipInput!.clear();
    // Reset any transient error state after an attempt to add via token end
    this.invalidEmailInput.set(false);
    this.emailInputTouched.set(true);
    this.emailsInputControl.reset('');
    // Clear required error if we now have emails
    if (this.userFormGroup.controls.emails.length > 0) {
      this.emailsInputControl.setErrors(null);
    }
  }

  // Add email on blur if valid; otherwise show inline error
  onEmailBlur(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    const raw = this.emailsInputControl.value || input?.value || '';
    const value = raw.trim();
    this.emailInputTouched.set(true);
    // Empty input: just revalidate array, do not flag format error
    if (!value) {
      this.invalidEmailInput.set(false);
      this.emailsInputControl.markAsTouched();
      // Check if emails array is empty - if so, mark control as invalid
      if (this.userFormGroup.controls.emails.length === 0) {
        this.emailsInputControl.setErrors({ required: true });
      } else {
        this.emailsInputControl.setErrors(null);
      }
      this.userFormGroup.controls.emails.updateValueAndValidity();
      return;
    }
    const isValidFormat = new FormControl(value, [Validators.email]).valid;
    if (isValidFormat) {
      this.addEmail(value);
      input.value = '';
      this.emailsInputControl.reset('');
      this.invalidEmailInput.set(false);
    } else {
      this.invalidEmailInput.set(true);
      this.emailsInputControl.setErrors({ email: true });
      this.emailsInputControl.markAsTouched();
    }
    this.userFormGroup.controls.emails.updateValueAndValidity();
  }

  // Clear error feedback while the user is editing
  onEmailInput(event: Event) {
    this.emailInputTouched.set(true);
    this.invalidEmailInput.set(false);
    this.emailsInputControl.setErrors(null);
    this.emailsInputControl.markAsTouched();
  }

  public organizationSelected(org: Organization | null) {
    this.userFormGroup.patchValue({
      organization: org,
      department: null,
    });
    this.resetDepartmentTrigger.set({ reset: true });
    if (org && !org.idTagGroupName) {
      this.snackbarService.error(
        'Der er ikke tilknyttet en id tag gruppe til organisationen. Derfor kan man ikke oprette bruger med brik til denne organisation'
      );
      this.departmentsDisabled.set(true);
      return;
    }
    if (org) {
      this.getAccessGroups(org?.id);
      this.userFormGroup.controls.department.setValue(null);
      this.departmentsDisabled.set(false);
    } else {
      this.departmentsDisabled.set(true);
    }
  }

  departmentSelected(department: Department | null) {
    this.userFormGroup.patchValue({
      department: department,
    });
  }
  save() {
    this.loadingService.setLoading(true);
    const organization = this.userFormGroup.controls.organization.value;
    const department = this.userFormGroup.controls.department.value;
    const selectedAccessGroups = this.userFormGroup.controls.selectedAccessGroups.value;
    if (!organization || !department) {
      this.loadingService.setLoading(false);
      return;
    }

    const model = new SendUserInvitations();
    model.organizationId = organization.id;
    model.departmentId = department.id;
    model.emails = this.userFormGroup.controls.emails.value;
    model.carType =
      this.userFormGroup.controls.userHasOrganizationCar.value == null
        ? undefined
        : this.userFormGroup.controls.userHasOrganizationCar.value
          ? AccessGroupCarType.CompanyCar
          : AccessGroupCarType.PrivateCar;
    model.accessGroupIdentifiers = selectedAccessGroups.map(x => x.identifier);
    this.userInvitationService.sendInvitations(model).subscribe({
      next: () => {
        this.snackbarService.success(`Invitationer er afsendt`);
        this.dialogRef.close();
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status == HttpStatusCode.BadRequest && httpError.error) {
          const error = httpError.error as ErrorMessage;
          if (error.code === 500011) {
            this.snackbarService.warning(`Email er ikke valid: ${error.message}`);
          }
        }
        if (httpError.status == HttpStatusCode.NotFound && httpError.error) {
          const error = httpError.error as ErrorMessage;
          if (error.code === 500010) {
            this.snackbarService.warning(`Organisation blev ikke fundet: ${error.message}`);
          }
        } else {
          this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error.message})`);
        }
      },
    });
  }
  public organizationIdValidator(control: AbstractControl): ValidationErrors | null {
    const org = control.value as Organization | null;
    if (!org || !org.id) {
      return { organizationIdRequired: true };
    }
    return null;
  }

  private getAccessGroups(organizationId: number | null) {
    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;
    const searchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(pageEvent);
    searchEvent.organizationId = organizationId;
    searchEvent.carType = this.userFormGroup.controls.userHasOrganizationCar.value
      ? AccessGroupCarType.CompanyCar
      : AccessGroupCarType.PrivateCar;
    this.accessGroupService.getAccessGroups(searchEvent).subscribe({
      next: result => {
        this.accessGroups.set(result.items);
        this.userFormGroup.controls.selectedAccessGroups.enable();
      },
    });
  }
}
